import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fromDate", "toDate", "rangeSelect"]
  
  connect() {
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);
    this.oneYearAgo = new Date();
    this.oneYearAgo.setFullYear(this.oneYearAgo.getFullYear() - 1);
    this.oneYearAgo.setHours(0, 0, 0, 0);

    this.setupDateInputs();

    // Listen for manual date changes
    if (this.hasFromDateTarget) {
      this.fromDateTarget.addEventListener('change', () => {
        this.onFromDateChange();
      });
    }
    if (this.hasToDateTarget) {
      this.toDateTarget.addEventListener('change', () => {
        this.onToDateChange();
      });
    }

    // Listen for range select changes
    if (this.hasRangeSelectTarget) {
      this.rangeSelectTarget.addEventListener('change', (event) => {
        this.onRangeSelect(event.target.value);
      });
    }
  }

  setupDateInputs(minDate, maxDate) {
    if (!minDate) {
      minDate = this.oneYearAgo;
    }
    if (!maxDate) {
      maxDate = this.today;
    }

    this.fromDateTarget.setAttribute('min', this.formatDate(minDate));
    this.fromDateTarget.setAttribute('max', this.formatDate(maxDate));
    this.toDateTarget.setAttribute('max', this.formatDate(maxDate));
    
    // Update toDate min based on current fromDate value
    if (this.fromDateTarget.value) {
      this.toDateTarget.setAttribute('min', this.fromDateTarget.value);
    } else {
      this.toDateTarget.setAttribute('min', this.formatDate(minDate));
    }
  }

  onFromDateChange() {
    if (this.fromDateTarget.value) {
      this.toDateTarget.setAttribute('min', this.fromDateTarget.value);
      
      if (this.toDateTarget.value && this.toDateTarget.value < this.fromDateTarget.value) {
        this.toDateTarget.value = this.fromDateTarget.value;
      }
    }
    
    this.setRangeSelectValue('custom');
  }

  onToDateChange() {
    this.setRangeSelectValue('custom');
  }

  onRangeSelect(range) {
    if (!range) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (range === 'today') {
      this.setDateRange(today, today);
    } else if (range === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      this.setDateRange(yesterday, yesterday);
    } else if (range === 'last_7_days') {
      const last7Days = new Date();
      last7Days.setDate(last7Days.getDate() - 7);
      last7Days.setHours(0, 0, 0, 0);
      this.setDateRange(last7Days, today);
    } else if (range === 'last_30_days') {
      const last30Days = new Date();
      last30Days.setDate(last30Days.getDate() - 30);
      last30Days.setHours(0, 0, 0, 0);
      this.setDateRange(last30Days, today);
    } else if (range === 'current_week') {
      const weekRange = this.getCurrentWeekRange();
      this.setDateRange(weekRange.start, weekRange.end);
    } else if (range === 'current_month') {
      const monthRange = this.getCurrentMonthRange();
      this.setDateRange(monthRange.start, monthRange.end);
    } else if (range === 'last_week') {
      const lastWeekRange = this.getLastWeekRange();
      this.setDateRange(lastWeekRange.start, lastWeekRange.end);
    } else if (range === 'last_month') {
      const lastMonthRange = this.getLastMonthRange();
      this.setDateRange(lastMonthRange.start, lastMonthRange.end);
    } else if (range === 'custom') {
      // Don't change the dates, just allow custom selection
      this.setupDateInputs();
    }
  }

  setDateRange(fromDate, toDate) {
    this.fromDateTarget.value = this.formatDate(fromDate);
    this.toDateTarget.value = this.formatDate(toDate);
    
    // Update input constraints based on the selected range
    this.fromDateTarget.setAttribute('min', this.formatDate(fromDate));
    this.fromDateTarget.setAttribute('max', this.formatDate(toDate));
    this.toDateTarget.setAttribute('min', this.formatDate(fromDate));
    this.toDateTarget.setAttribute('max', this.formatDate(toDate));
  }

  formatDate(date) {
    // Format date as yyyy-mm-dd for HTML5 date inputs
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getCurrentWeekRange() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so 6 days back to Monday
    startOfWeek.setDate(today.getDate() - daysToMonday);
    const endOfWeek = new Date(today);
    
    return { start: startOfWeek, end: endOfWeek };
  }

  getCurrentMonthRange() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today);
    
    return { start: startOfMonth, end: endOfMonth };
  }

  getLastWeekRange() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startOfLastWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfLastWeek.setDate(today.getDate() - daysToMonday - 7);
    
    const endOfLastWeek = new Date(startOfLastWeek);
    endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
    
    return { start: startOfLastWeek, end: endOfLastWeek };
  }

  getLastMonthRange() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    
    return { start: startOfLastMonth, end: endOfLastMonth };
  }

  setRangeSelectValue(value) {
    const choicesController = this.application.getControllerForElementAndIdentifier(this.rangeSelectTarget, 'choices');
    
    if (choicesController && choicesController.choices) {
      // Use the Choices.js instance to set the value
      choicesController.choices.setChoiceByValue(value);
    } else {
      // Fallback: try to set the value directly on the select element
      this.rangeSelectTarget.value = value;
      // Trigger change event to notify other listeners
      this.rangeSelectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}